import { getOssToken } from '../ReactAdminOss._Core/src/security/tokenHelper';

const baseUrl = process.env.REACT_APP_TECHNICIAN_APP_CHAT_API;

// Чатът е само в средите, чийто Config/<среда>/config.env дава адреса му; дали е включен в момента — ChatSwitch.
const chatConfigured = !!baseUrl;

const requestTimeoutMs = 10000;
const configTimeoutMs = 5000;
const attachmentTimeoutMs = 60000;
// Изборът на тема чака и автоматичния отговор — ping/рестарт през Cempresso отговаря до 30 сек.
const topicTimeoutMs = 60000;

// Чатът е на същия хост като API-то на таблета, а браузърът държи най-много 6 връзки към хост: увиснала заявка към
// чата не бива да задържа задачите.
const fetchWithTimeout = async (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// Пауза преди повторното четене — случайна, за да не пробват всички таблети пак в една и съща секунда след рестарт
// на сървиса (анализът за скалирането, 25.10.2026).
const readRetryPause = () =>
  new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

// Не минава през asyncFetch: неговият логър се чупи при неочакван payload, а 409 тук е нормален отговор.
// Мобилната връзка на таблета понякога изпуска заявка, докато сървисът отговаря на другите (22.10.2026 — „Чатът не
// отговаря." при отваряне на общия чат): четенето се пробва още веднъж, след кратка пауза; записите — не, за да не се
// повторят.
const request = async (path, options = {}, timeoutMs = requestTimeoutMs) => {
  const send = () =>
    fetchWithTimeout(
      `${baseUrl}${path}`,
      {
        ...options,
        mode: 'cors',
        headers: {
          accept: 'application/json',
          // Снимката отива като multipart — границата в заглавката я слага браузърът.
          ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
          authorization: getOssToken(),
        },
      },
      timeoutMs
    );
  const isRead = !options.method || options.method === 'GET';

  let response;
  try {
    response = await send().catch(async e => {
      if (!isRead) throw e;
      await readRetryPause();
      return send();
    });
  } catch (e) {
    throw new Error(
      e.name === 'AbortError' ? 'Чатът не отговаря.' : 'Няма връзка със сървъра на чата.'
    );
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error((data && data.title) || `Грешка ${response.status}`);
    error.status = response.status;
    error.existing = data && data.existing;
    throw error;
  }

  return data;
};

// Настройките на чата (включен ли е, снимки); при грешка или таймаут — null (чатът се скрива).
const getChatConfig = async () => {
  if (!chatConfigured) return null;
  try {
    const response = await fetchWithTimeout(
      `${baseUrl}/chat/config`,
      { headers: { accept: 'application/json' } },
      configTimeoutMs
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    return null;
  }
};

// Има ли чат за логнатия (техник или координатор): true / false; при грешка или таймаут — null (пита се пак).
const getChatAccess = async () => {
  try {
    const access = await request('/chat/access', {}, configTimeoutMs);
    return !!(access && access.enabled);
  } catch (e) {
    return null;
  }
};

// Изтекла ли е сесията — за хъба, когато не успее да се свърже: true само при 401; грешка в мрежата или таймаут —
// false (хъбът пробва пак).
const isSessionRejected = async () => {
  try {
    await request('/chat/access', {}, configTimeoutMs);
    return false;
  } catch (e) {
    return e.status === 401;
  }
};

const getMySchedules = () => request('/my/schedules');

const getActiveChats = () => request('/my/active-chats');

const getTaskChats = scheduleTaskId => request(`/tasks/${scheduleTaskId}/chats`);

const createTaskChat = scheduleTaskId =>
  request(`/tasks/${scheduleTaskId}/chats`, { method: 'POST' });

const getScheduleChats = scheduleByDateId => request(`/schedules/${scheduleByDateId}/chats`);

const createScheduleChat = scheduleByDateId =>
  request(`/schedules/${scheduleByDateId}/chats`, { method: 'POST' });

const getMessages = (chatId, afterId) =>
  request(`/chats/${chatId}/messages${afterId ? `?afterId=${afterId}` : ''}`);

const sendMessage = (chatId, clientMessageId, body, attachmentIds) =>
  request(`/chats/${chatId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ clientMessageId, body, attachmentIds }),
  });

const uploadAttachment = (chatId, blob) => {
  const form = new FormData();
  form.append('file', blob, 'snimka.jpg');
  return request(
    `/chats/${chatId}/attachments`,
    { method: 'POST', body: form },
    attachmentTimeoutMs
  );
};

// Снимките не са с публичен линк — дърпат се с токена и се пазят в паметта като object URL.
const attachmentUrls = new Map();

const getAttachmentUrl = attachmentId => {
  if (!attachmentUrls.has(attachmentId)) {
    const url = (async () => {
      const response = await fetchWithTimeout(
        `${baseUrl}/attachments/${attachmentId}`,
        { mode: 'cors', headers: { authorization: getOssToken() } },
        attachmentTimeoutMs
      );
      if (!response.ok) throw new Error(`Грешка ${response.status}`);
      return URL.createObjectURL(await response.blob());
    })();
    attachmentUrls.set(attachmentId, url);
    url.catch(() => attachmentUrls.delete(attachmentId));
  }
  return attachmentUrls.get(attachmentId);
};

// Току-що качената снимка вече е на таблета — не се сваля обратно.
const rememberAttachmentUrl = (attachmentId, url) =>
  attachmentUrls.set(attachmentId, Promise.resolve(url));

const closeChat = chatId => request(`/chats/${chatId}/close`, { method: 'POST' });

const getReceipts = chatId => request(`/chats/${chatId}/receipts`);

const sendReceipt = (chatId, body) =>
  request(`/chats/${chatId}/receipts`, { method: 'POST', body: JSON.stringify(body) });

// Незавършените ми чатове — отбелязва ги като получени и връща кои да се следят на заден план.
const markActiveChatsDelivered = () => request('/my/active-chats/delivered', { method: 'POST' });

// generalChat — темите на общия чат на графика (27.10.2026), иначе — на чата по задача.
const getCategories = (withAutoAnswers, generalChat = false) =>
  request(
    `/categories?withAutoAnswers=${withAutoAnswers ? 'true' : 'false'}${
      generalChat ? '&generalChat=true' : ''
    }`
  );

// Готовите текстове на техника в общия чат („Готови текстове" в администрацията на графиците).
const getQuickReplies = () => request('/quick-replies');

// confirmed — техникът е потвърдил действието (тема с requiresConfirmation — рестарт на устройство).
const selectCategory = (chatId, clientMessageId, categoryId, confirmed = false) =>
  request(
    `/chats/${chatId}/select-category`,
    {
      method: 'POST',
      body: JSON.stringify({ clientMessageId, categoryId, confirmed }),
    },
    topicTimeoutMs
  );

const escalateChat = chatId => request(`/chats/${chatId}/escalate`, { method: 'POST' });

// crypto.randomUUID иска https, а тестовата среда е на http.
const newClientMessageId = () =>
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
    // eslint-disable-next-line no-bitwise
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

export {
  chatConfigured,
  getChatConfig,
  getChatAccess,
  isSessionRejected,
  getMySchedules,
  getActiveChats,
  getTaskChats,
  createTaskChat,
  getScheduleChats,
  createScheduleChat,
  getMessages,
  sendMessage,
  uploadAttachment,
  getAttachmentUrl,
  rememberAttachmentUrl,
  closeChat,
  getReceipts,
  sendReceipt,
  markActiveChatsDelivered,
  getCategories,
  getQuickReplies,
  selectCategory,
  escalateChat,
  newClientMessageId,
};
